import type {
  AccountHolding,
  ParsedOption,
  HoldingsLeg,
  HoldingsStrategy,
} from './brokerage.types.ts';
import type { OptionQuote } from '../../api/massiveApi.ts';

/**
 * Parse OCC option symbol to extract details
 * Format: "NVDA  260515P00190000" = NVDA, 2026-05-15, PUT, $190
 */
export function parseOptionSymbol(occSymbol: string): ParsedOption | null {
  if (!occSymbol) return null;

  const trimmed = occSymbol.trim().replace(/^O:/, '').replace(/\s+/g, '');
  if (trimmed.length < 15) return null;

  const strikeStr = trimmed.slice(-8);
  const typeChar = trimmed.slice(-9, -8);
  const dateStr = trimmed.slice(-15, -9);
  const underlying = trimmed.slice(0, -15).trim();

  if (!underlying || !/^\d{6}$/.test(dateStr) || !['C', 'P'].includes(typeChar)) {
    return null;
  }

  const year = 2000 + parseInt(dateStr.slice(0, 2), 10);
  const month = parseInt(dateStr.slice(2, 4), 10);
  const day = parseInt(dateStr.slice(4, 6), 10);
  const expiration = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const expDate = new Date(year, month - 1, day);
  const expirationDisplay = expDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
  });

  const strike = parseInt(strikeStr, 10) / 1000;

  return {
    underlying,
    expiration,
    expirationDisplay,
    type: typeChar === 'C' ? 'CALL' : 'PUT',
    strike,
  };
}

/**
 * Identify strategy type based on legs
 */
function identifyHoldingsStrategyType(legs: HoldingsLeg[], hasStock: boolean): string {
  const optionLegs = legs.filter(l => l.parsed);

  // Covered call: long stock + short call
  if (hasStock) {
    const shortCalls = optionLegs.filter(l => l.parsed?.type === 'CALL' && l.direction === 'short');
    if (shortCalls.length > 0) {
      // Check if it's a Poor Man's Covered Call (long call instead of stock)
      const longCalls = optionLegs.filter(l => l.parsed?.type === 'CALL' && l.direction === 'long');
      if (longCalls.length > 0) {
        return "Poor Man's Covered Call";
      }
      return 'Covered Call';
    }
    const shortPuts = optionLegs.filter(l => l.parsed?.type === 'PUT' && l.direction === 'short');
    if (shortPuts.length > 0) {
      return 'Cash-Secured Put';
    }
    return 'Stock Position';
  }

  if (optionLegs.length === 0) {
    return 'Stock Position';
  }

  if (optionLegs.length === 1) {
    const leg = optionLegs[0];
    const typeStr = leg.parsed?.type === 'CALL' ? 'Call' : 'Put';
    return leg.direction === 'long' ? `Long ${typeStr}` : `Short ${typeStr}`;
  }

  if (optionLegs.length === 2) {
    const types = optionLegs.map(l => l.parsed?.type || '').sort();
    const directions = optionLegs.map(l => l.direction || '').sort();
    const expirations = optionLegs.map(l => l.parsed?.expiration);
    const strikes = optionLegs.map(l => l.parsed?.strike ?? 0).sort((a, b) => a - b);

    const sameExpiration = expirations[0] === expirations[1];
    const sameStrike = strikes[0] === strikes[1];
    const sameType = types[0] === types[1];
    const oppositeDirections = directions[0] !== directions[1];

    // Poor Man's Covered Call: long deep ITM call + short OTM call
    if (sameType && types[0] === 'CALL' && oppositeDirections && !sameExpiration) {
      const longCall = optionLegs.find(l => l.direction === 'long');
      const shortCall = optionLegs.find(l => l.direction === 'short');
      if (longCall && shortCall) {
        const longExp = new Date(longCall.parsed!.expiration);
        const shortExp = new Date(shortCall.parsed!.expiration);
        if (longExp > shortExp) {
          return "Poor Man's Covered Call";
        }
      }
      return 'Call Diagonal';
    }

    // Calendar spread: same strike, same type, different expirations
    if (sameStrike && sameType && !sameExpiration && oppositeDirections) {
      return types[0] === 'CALL' ? 'Call Calendar' : 'Put Calendar';
    }

    // Diagonal spread: different strikes, same type, different expirations
    if (!sameStrike && sameType && !sameExpiration && oppositeDirections) {
      return types[0] === 'CALL' ? 'Call Diagonal' : 'Put Diagonal';
    }

    // Vertical spread: same expiration, different strikes, same type
    if (sameExpiration && !sameStrike && sameType && oppositeDirections) {
      return types[0] === 'CALL' ? 'Call Spread' : 'Put Spread';
    }

    // Straddle: same strike, different types
    if (sameStrike && !sameType) {
      const allLong = directions.every(d => d === 'long');
      const allShort = directions.every(d => d === 'short');
      if (allLong) return 'Long Straddle';
      if (allShort) return 'Short Straddle';
    }

    // Strangle: different strikes, different types
    if (!sameStrike && !sameType) {
      const allLong = directions.every(d => d === 'long');
      const allShort = directions.every(d => d === 'short');
      if (allLong) return 'Long Strangle';
      if (allShort) return 'Short Strangle';
    }
  }

  if (optionLegs.length === 4) {
    const types = optionLegs.map(l => l.parsed?.type);
    const calls = types.filter(t => t === 'CALL').length;
    const puts = types.filter(t => t === 'PUT').length;

    if (calls === 2 && puts === 2) {
      return 'Iron Condor';
    }
    if (calls === 4 || puts === 4) {
      return 'Butterfly';
    }
  }

  return `${optionLegs.length}-Leg Strategy`;
}

/**
 * Group holdings into strategies based on underlying and expiration
 */
export function groupHoldingsIntoStrategies(
  holdings: AccountHolding[],
  liveQuotes: Map<string, OptionQuote>
): HoldingsStrategy[] {
  const strategies: HoldingsStrategy[] = [];

  console.group('🔍 DEBUG: Strategy Grouping & P&L Calculations');

  // DEBUG: Log all holdings data at entry
  console.log('📥 Raw holdings received:', holdings.length);
  holdings.forEach((h, i) => {
    console.log(`  [${i}] symbol=${h.symbol}, underlying=${h.underlyingSymbol}, optionSymbol=${h.optionSymbol}, isOption=${h.isOption}, units=${h.units}`);
  });

  // Separate options and stocks by underlying
  const optionsByUnderlying = new Map<string, AccountHolding[]>();
  const stocksBySymbol = new Map<string, AccountHolding[]>();

  for (const holding of holdings) {
    if (holding.isOption) {
      const underlying = holding.underlyingSymbol || holding.symbol;
      const existing = optionsByUnderlying.get(underlying) || [];
      existing.push(holding);
      optionsByUnderlying.set(underlying, existing);
    } else {
      const existing = stocksBySymbol.get(holding.symbol) || [];
      existing.push(holding);
      stocksBySymbol.set(holding.symbol, existing);
    }
  }

  // Helper to create a leg from a holding
  function createLeg(holding: AccountHolding): HoldingsLeg {
    const parsed = parseOptionSymbol(holding.optionSymbol || '');
    const direction: 'long' | 'short' = holding.units >= 0 ? 'long' : 'short';
    const quantity = Math.abs(holding.units);

    // Get live price from Massive API
    const quote = liveQuotes.get(holding.optionSymbol || '');
    const currentPrice = quote?.fmv ?? quote?.close ?? holding.currentPrice ?? 0;

    // SnapTrade's averagePurchasePrice is per-contract, not per-share
    // Divide by 100 to get per-share cost for comparison with FMV
    const avgCostPerContract = holding.averagePurchasePrice ?? 0;
    const avgCost = avgCostPerContract / 100;

    // Calculate values (options are 100 shares per contract)
    // For short positions, market value is negative (liability) and cost basis is negative (credit received)
    const rawValue = quantity * currentPrice * 100;
    const rawCost = quantity * avgCost * 100;

    const marketValue = direction === 'long' ? rawValue : -rawValue;
    const costBasis = direction === 'long' ? rawCost : -rawCost;

    // P&L: For long positions, profit when value increases. For short, profit when value decreases.
    // With signed values: unrealizedPnl = marketValue - costBasis works for both
    // Long: +$100 value - +$80 cost = +$20 profit
    // Short: -$60 value - -$80 cost = +$20 profit (price dropped from $80 to $60)
    const unrealizedPnl = marketValue - costBasis;

    const unrealizedPnlPercent = costBasis !== 0
      ? (unrealizedPnl / Math.abs(costBasis)) * 100
      : 0;

    // DEBUG: Log each leg calculation
    console.group(`📊 Leg: ${holding.optionSymbol || holding.symbol}`);
    console.log('Brokerage Data:');
    console.log('  - units (raw):', holding.units);
    console.log('  - averagePurchasePrice (per-contract):', avgCostPerContract);
    console.log('  - currentPrice (from broker):', holding.currentPrice);
    console.log('  - marketValue (from broker):', holding.marketValue);
    console.log('  - unrealizedPnl (from broker):', holding.unrealizedPnl);
    console.log('Massive API Data:');
    console.log('  - quote found:', !!quote);
    console.log('  - fmv (per-share):', quote?.fmv);
    console.log('  - close (per-share):', quote?.close);
    console.log('Calculated Values:');
    console.log('  - direction:', direction);
    console.log('  - quantity (contracts):', quantity);
    console.log('  - avgCost (per-share, /100):', avgCost);
    console.log('  - currentPrice (per-share):', currentPrice);
    console.log('  - rawCost:', rawCost, '→ costBasis:', costBasis, `(${direction})`);
    console.log('  - rawValue:', rawValue, '→ marketValue:', marketValue, `(${direction})`);
    console.log('  - unrealizedPnl:', unrealizedPnl, '=', marketValue, '-', costBasis);
    console.log('  - unrealizedPnlPercent:', unrealizedPnlPercent.toFixed(2) + '%');
    console.groupEnd();

    return {
      holding,
      parsed,
      direction,
      quantity,
      avgCost,
      currentPrice,
      marketValue,
      costBasis,
      unrealizedPnl,
      unrealizedPnlPercent,
    };
  }

  // Group options by underlying, then match multi-leg strategies
  for (const [underlying, options] of optionsByUnderlying) {
    // Check if there's a matching stock position for covered calls
    const stockPositions = stocksBySymbol.get(underlying) || [];
    const hasStock = stockPositions.length > 0 && stockPositions.some(s => s.units > 0);

    // Parse all options and track which have been matched
    const parsedOptions = options.map(opt => ({
      holding: opt,
      parsed: parseOptionSymbol(opt.optionSymbol || ''),
      direction: (opt.units >= 0 ? 'long' : 'short') as 'long' | 'short',
      quantity: Math.abs(opt.units),
      matched: false,
    }));

    // First pass: match diagonals/calendars (same type, opposite direction, different expiration)
    const matchedStrategies: AccountHolding[][] = [];

    for (let i = 0; i < parsedOptions.length; i++) {
      if (parsedOptions[i].matched) continue;
      const opt1 = parsedOptions[i];
      if (!opt1.parsed) continue;

      // Look for matching leg (same type, opposite direction, same quantity)
      for (let j = i + 1; j < parsedOptions.length; j++) {
        if (parsedOptions[j].matched) continue;
        const opt2 = parsedOptions[j];
        if (!opt2.parsed) continue;

        const sameType = opt1.parsed.type === opt2.parsed.type;
        const oppositeDirection = opt1.direction !== opt2.direction;
        const sameQuantity = opt1.quantity === opt2.quantity;
        const differentExpiration = opt1.parsed.expiration !== opt2.parsed.expiration;

        if (sameType && oppositeDirection && sameQuantity && differentExpiration) {
          // Found a diagonal/calendar spread
          matchedStrategies.push([opt1.holding, opt2.holding]);
          parsedOptions[i].matched = true;
          parsedOptions[j].matched = true;
          console.log(`🔗 Matched diagonal: ${opt1.holding.optionSymbol} + ${opt2.holding.optionSymbol}`);
          break;
        }
      }
    }

    // Second pass: group remaining unmatched options by expiration (verticals, single legs)
    const unmatchedByExpiration = new Map<string, AccountHolding[]>();
    for (const opt of parsedOptions) {
      if (opt.matched) continue;
      const exp = opt.parsed?.expiration || 'unknown';
      const existing = unmatchedByExpiration.get(exp) || [];
      existing.push(opt.holding);
      unmatchedByExpiration.set(exp, existing);
    }

    // Create strategies from matched diagonals/calendars
    for (const holdings of matchedStrategies) {
      const legs = holdings.map(createLeg);
      const expirations = legs.map(l => l.parsed?.expiration).filter(Boolean).sort();
      const expDisplay = expirations.join('/');

      const totalMarketValue = legs.reduce((sum, l) => sum + l.marketValue, 0);
      const totalCostBasis = legs.reduce((sum, l) => sum + l.costBasis, 0);
      const totalUnrealizedPnl = legs.reduce((sum, l) => sum + l.unrealizedPnl, 0);
      const totalUnrealizedPnlPercent = totalCostBasis > 0
        ? (totalUnrealizedPnl / totalCostBasis) * 100
        : 0;

      strategies.push({
        id: `${underlying}-${expDisplay}`,
        underlying,
        strategyType: identifyHoldingsStrategyType(legs, hasStock),
        legs,
        totalMarketValue,
        totalCostBasis,
        totalUnrealizedPnl,
        totalUnrealizedPnlPercent,
        expiration: expirations[expirations.length - 1] || null, // Use later expiration
        isStock: false,
      });
    }

    // Create strategies from unmatched options grouped by expiration
    for (const [expiration, expOptions] of unmatchedByExpiration) {
      const legs = expOptions.map(createLeg);

      const totalMarketValue = legs.reduce((sum, l) => sum + l.marketValue, 0);
      const totalCostBasis = legs.reduce((sum, l) => sum + l.costBasis, 0);
      const totalUnrealizedPnl = legs.reduce((sum, l) => sum + l.unrealizedPnl, 0);
      const totalUnrealizedPnlPercent = totalCostBasis > 0
        ? (totalUnrealizedPnl / totalCostBasis) * 100
        : 0;

      strategies.push({
        id: `${underlying}-${expiration}`,
        underlying,
        strategyType: identifyHoldingsStrategyType(legs, hasStock),
        legs,
        totalMarketValue,
        totalCostBasis,
        totalUnrealizedPnl,
        totalUnrealizedPnlPercent,
        expiration: expiration !== 'unknown' ? expiration : null,
        isStock: false,
      });
    }

    // Remove stock from stocksBySymbol if used in covered call
    if (hasStock) {
      stocksBySymbol.delete(underlying);
    }
  }

  // Add remaining stock positions as standalone strategies
  for (const [symbol, stocks] of stocksBySymbol) {
    for (const holding of stocks) {
      const direction: 'long' | 'short' = holding.units >= 0 ? 'long' : 'short';
      const quantity = Math.abs(holding.units);
      const currentPrice = holding.currentPrice ?? 0;
      const avgCost = holding.averagePurchasePrice ?? 0;
      const marketValue = quantity * currentPrice;
      const costBasis = quantity * avgCost;

      let unrealizedPnl: number;
      if (direction === 'long') {
        unrealizedPnl = marketValue - costBasis;
      } else {
        unrealizedPnl = costBasis - marketValue;
      }

      const unrealizedPnlPercent = costBasis > 0
        ? (unrealizedPnl / costBasis) * 100
        : 0;

      const leg: HoldingsLeg = {
        holding,
        parsed: null,
        direction,
        quantity,
        avgCost,
        currentPrice,
        marketValue,
        costBasis,
        unrealizedPnl,
        unrealizedPnlPercent,
      };

      strategies.push({
        id: `stock-${symbol}`,
        underlying: symbol,
        strategyType: direction === 'long' ? 'Long Stock' : 'Short Stock',
        legs: [leg],
        totalMarketValue: marketValue,
        totalCostBasis: costBasis,
        totalUnrealizedPnl: unrealizedPnl,
        totalUnrealizedPnlPercent: unrealizedPnlPercent,
        expiration: null,
        isStock: true,
      });
    }
  }

  // Sort by underlying, then by expiration
  strategies.sort((a, b) => {
    const underlyingA = a.underlying || '';
    const underlyingB = b.underlying || '';
    if (underlyingA !== underlyingB) {
      return underlyingA.localeCompare(underlyingB);
    }
    if (a.expiration && b.expiration) {
      return a.expiration.localeCompare(b.expiration);
    }
    return 0;
  });

  console.log('📋 Final Strategies:', strategies.length);
  strategies.forEach(s => {
    console.log(`  - ${s.underlying} ${s.strategyType}: MV=${s.totalMarketValue.toFixed(2)}, PnL=${s.totalUnrealizedPnl.toFixed(2)}`);
  });
  console.groupEnd();

  return strategies;
}
